import { ChevronDownIcon } from "@cynthai/icons";
import { Button } from "@cynthai/ui";
import { useDisclosure } from "@reactuses/core";
import { Link as RouterLink, useRouterState } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useEffect } from "react";
import { twMerge } from "tailwind-merge";

import type { SidebarItem } from "@/core/lib/sidebar-types";

import { sidebarContentStyles, sidebarItemStyles } from "./styles";

const ACTIVE_LINK_PROPS = { "aria-current": "page" as const };

type SidebarMainLinkProps = Readonly<{
  to: SidebarItem["to"];
  label: string;
  forceActive: boolean;
  className: string;
  children: ReactNode;
  onNavigate?: () => void;
}>;

function SidebarMainLink({
  to,
  label,
  forceActive,
  className,
  children,
  onNavigate,
}: SidebarMainLinkProps) {
  return (
    <RouterLink
      {...(typeof to === "string" ? { to } : to)}
      activeOptions={{
        exact: (typeof to === "string" ? to : to.to) === "/",
      }}
      activeProps={forceActive ? undefined : ACTIVE_LINK_PROPS}
      aria-current={forceActive ? "page" : undefined}
      className={twMerge(
        className,
        forceActive && "bg-sidebar text-text-sidebar",
      )}
      onClick={onNavigate}
      title={label}
    >
      {children}
    </RouterLink>
  );
}

type SidebarSubItemLinksProps = Readonly<{
  subItems: SidebarItem["subItems"];
  isMenuOpen: boolean;
  onNavigate?: () => void;
}>;

type SidebarItemProps = SidebarItem & {
  readonly isMenuOpen: boolean;
  readonly onNavigate?: () => void;
};

function SidebarSubItemLinks({
  subItems,
  isMenuOpen,
  onNavigate,
}: SidebarSubItemLinksProps) {
  return subItems?.map(({ label: subLabel, to: subTo, exact }) => (
    <RouterLink
      key={subLabel}
      {...(typeof subTo === "string" ? { to: subTo } : subTo)}
      activeOptions={{ exact: exact ?? false }}
      activeProps={ACTIVE_LINK_PROPS}
      onClick={onNavigate}
      className={sidebarItemStyles({ expanded: isMenuOpen, subitem: true })}
      title={subLabel}
    >
      {isMenuOpen ? subLabel : subLabel.charAt(0)}
    </RouterLink>
  ));
}

export function SidebarItem({
  label,
  to,
  subItems,
  activePathnamePrefix,
  icon: IconComponent,
  isMenuOpen,
  onNavigate,
}: SidebarItemProps) {
  const { onOpenChange, isOpen } = useDisclosure({ defaultOpen: false });
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const hasSubItems = !!subItems?.length;
  const forceActive =
    !hasSubItems &&
    activePathnamePrefix != null &&
    pathname.startsWith(activePathnamePrefix);

  useEffect(() => {
    if (!isMenuOpen && isOpen) {
      onOpenChange();
    }
  }, [isMenuOpen, isOpen, onOpenChange]);

  const mainItemClassName = sidebarItemStyles({
    expanded: isMenuOpen,
  });
  const { label: labelClassName, chevronWrapper: chevronClassName } =
    sidebarContentStyles({ expanded: isMenuOpen });

  const mainItemContent = (
    <>
      <IconComponent
        className="shrink-0"
        width={20}
        height={20}
        size={20}
        color="currentColor"
      />
      <span aria-hidden={!isMenuOpen} className={labelClassName()}>
        {label}
      </span>
      {hasSubItems ? (
        <span aria-hidden={!isMenuOpen} className={chevronClassName()}>
          <ChevronDownIcon
            width={20}
            height={20}
            className={twMerge(
              "shrink-0 transition-transform duration-200",
              isOpen && "rotate-180",
            )}
          />
        </span>
      ) : null}
    </>
  );

  const subItemMain = (
    <Button
      aria-expanded={isMenuOpen ? isOpen : undefined}
      data-expanded={isMenuOpen ? isOpen : undefined}
      onPress={isMenuOpen ? () => onOpenChange() : undefined}
      className={mainItemClassName}
      aria-label={label}
    >
      {mainItemContent}
    </Button>
  );

  const mainItem = hasSubItems ? (
    subItemMain
  ) : (
    <SidebarMainLink
      to={to}
      label={label}
      forceActive={forceActive}
      className={mainItemClassName}
      onNavigate={onNavigate}
    >
      {mainItemContent}
    </SidebarMainLink>
  );

  return (
    <div
      className={twMerge(
        isMenuOpen && "w-full",
        `group/sidebar-item-wrapper relative *:mt-2 *:first:mt-0`,
        hasSubItems &&
          isMenuOpen &&
          `before:bg-tertiary before:absolute before:top-12 before:bottom-0 before:left-6 before:w-0.5 before:rounded-l-lg`,
      )}
    >
      {mainItem}
      <SidebarSubItemLinks
        subItems={subItems}
        isMenuOpen={isMenuOpen}
        onNavigate={onNavigate}
      />
    </div>
  );
}
